import Vue, { computed, ref, watch } from 'vue'
import { useConfig } from '@/composables/useConfig'
import { defaultOf } from '@/config/configSchema'

/**
 * Everything about the columns of the vehicles table: what they are, which
 * ones the user picked, and every way that selection can change.
 *
 * `TrucksTable.vue` takes `headers` from here; the settings list takes the
 * rest. The selection lives in the `vehicleListTableFields` user config, so
 * both stay in step through the Config Bus without talking to each other.
 */

/**
 * Column catalogue for the vehicles table.
 *
 * Pure metadata — no markup. Each entry is keyed by the value stored in the
 * `vehicleListTableFields` setting; `TrucksTable.vue` turns the selected ones
 * into `DataTable` headers and renders each through its `item.<key>` slot.
 *
 * - `label`     translation key for the header text
 * - `icon`      Font Awesome class, shown next to the label in the settings.
 *               Same glyph the rest of the app uses for that concept (mostly
 *               `TruckListItem.vue`, which shows the same fields in list view)
 * - `sortable`  whether the header can be clicked to sort
 * - `sortKey`   path the value is read from, when it differs from the key
 *
 * No widths here: `DataTable` is a real table, so every column ends up as wide
 * as its own content. A column that really needs a width of its own gets it
 * from the stylesheet in `TrucksTable.vue`.
 */
const TRUCK_COLUMNS = {
    status: { label: 'status', icon: 'fa-solid fa-circle', sortKey: 'activity_type' },
    plate: { label: 'plate', icon: 'fa-solid fa-truck', sortable: true, sortKey: 'plate' },
    custom_id: {
        label: 'custom_id',
        icon: 'fa-solid fa-hashtag',
        sortable: true,
        sortKey: 'details.custom_id'
    },
    activity: {
        label: 'activity',
        icon: 'fa-solid fa-steering-wheel',
        sortKey: 'lastpoint.activity'
    },
    codriver: {
        label: 'codriver',
        icon: 'fa-solid fa-user-group',
        sortKey: 'lastpoint.codriver'
    },
    speed: {
        label: 'speed',
        icon: 'fa-solid fa-gauge',
        sortable: true,
        sortKey: 'lastpoint.speed'
    },
    tacho_speed: {
        label: 'tacho_speed',
        icon: 'fa-solid fa-gauge',
        sortable: true,
        sortKey: 'lastpoint.tacho_speed'
    },
    heading: { label: 'heading', icon: 'fa-solid fa-arrow-up', sortKey: 'lastpoint.heading' },
    altitude: {
        label: 'altitude',
        icon: 'fa-solid fa-mountain',
        sortable: true,
        sortKey: 'lastpoint.altitude'
    },
    battery_level: {
        label: 'battery_level',
        icon: 'fa-solid fa-battery',
        sortable: true,
        sortKey: 'lastpoint.telemetry.battery_level'
    },
    fuel_level: {
        label: 'fuel_level',
        icon: 'fa-solid fa-gas-pump',
        sortable: true,
        sortKey: 'lastpoint.fuel_level'
    },
    rpm_engine: {
        label: 'rpm_engine',
        icon: 'fa-solid fa-tachometer-alt',
        sortable: true,
        sortKey: 'lastpoint.engine_rpm'
    },
    ignition: { label: 'ignition', icon: 'fa-solid fa-power-off', sortKey: 'lastpoint.ignition' },
    odometer: {
        label: 'odometer',
        icon: 'fa-solid fa-road',
        sortable: true,
        sortKey: 'lastpoint.odometer'
    },
    gps_odometer: {
        label: 'odometer_gps',
        icon: 'fa-solid fa-road',
        sortable: true,
        sortKey: 'lastpoint.gps_odometer'
    },
    daily_distance: { label: 'daily_distance', icon: 'fa-solid fa-route' },
    gnss_status: {
        label: 'gnss_status',
        icon: 'fa-solid fa-satellite-dish',
        sortKey: 'lastpoint.telemetry.gnss_status'
    },
    gsm_signal: {
        label: 'gsm_signal',
        icon: 'fa-solid fa-signal',
        sortKey: 'lastpoint.telemetry.gsm_signal'
    },

    // Driver plafond (driving hours)
    daily_driving: { label: 'plafonds.daily_driving', icon: 'fa-solid fa-clock' },
    driving_to_next_break: {
        label: 'plafonds.driving_to_next_break',
        icon: 'fa-solid fa-bed'
    },
    residual_commitment: {
        label: 'plafonds.residual_commitment',
        icon: 'fa-solid fa-hourglass-half'
    },

    address: { label: 'address', icon: 'fa-solid fa-location-dot' },
    // Reverse geocoded like the address, from the same cached response
    nearest_city: { label: 'nearest_city', icon: 'fa-solid fa-city' },
    alerts: { label: 'alerts', icon: 'fa-solid fa-triangle-exclamation' },

    // The row buttons (plan, activity). A column like any other: it can be
    // hidden and moved, and `TrucksTable` renders it through its own slot.
    actions: { label: 'actions', icon: 'fa-solid fa-bolt' }
}

// Shown for a column whose catalogue entry has no `icon` of its own.
const DEFAULT_COLUMN_ICON = 'fa-solid fa-table-columns'

/**
 * The saved `vehicleListTableFields` turned into a usable list of column keys:
 * anything the catalogue no longer knows about is dropped, duplicates are
 * dropped, and the user's order is kept.
 *
 * Entries used to be whole `{ value, label }` options rather than keys, so a
 * config in that shape is one saved before `actions` became a column of its
 * own — back then the row buttons came after the picked columns and could not
 * be turned off, so those configs keep them. Anything saved since is taken
 * literally and `actions` hides like any other column.
 */
function normalizeTableFields(saved) {
    const entries = Array.isArray(saved?.fields) ? saved.fields : []

    // An entry that is an object rather than a key is the old `{ value, label }`
    // shape, which dates the config: back then the row buttons were bolted on
    // after the picked columns and could not be turned off, so those configs
    // keep them. Anything saved since is taken literally.
    const savedBeforeActionsExisted = entries.some((entry) => entry && typeof entry === 'object')

    const keys = entries.map((entry) => (typeof entry === 'string' ? entry : entry?.value))
    const known = keys.filter((key) => TRUCK_COLUMNS[key])
    const unique = known.filter((key, index) => known.indexOf(key) === index)

    if (savedBeforeActionsExisted && !unique.includes('actions')) unique.push('actions')

    return unique
}

export function useTruckColumns() {
    const { value: saved, set: saveConfig } = useConfig('vehicleListTableFields')

    /** The picked columns, cleaned up, in the order the user arranged them. */
    const selected = computed(() => normalizeTableFields(saved.value))

    /** Those same columns as `DataTable` headers. */
    const headers = computed(() =>
        selected.value.map((key) => ({
            value: key,
            text: label(key),
            sortable: TRUCK_COLUMNS[key].sortable === true,
            sortKey: TRUCK_COLUMNS[key].sortKey
        }))
    )

    // ---- The settings list ------------------------------------------------

    const search = ref('')

    // Working copy of the order: `draggable` needs something it may reorder.
    const order = ref([])
    watch(selected, (keys) => (order.value = [...keys]), { immediate: true })

    /** The picked columns, filtered by the search. */
    const shown = computed(() => order.value.filter(matches))

    /** Everything else, in catalogue order. */
    const hidden = computed(() =>
        Object.keys(TRUCK_COLUMNS).filter((key) => !order.value.includes(key) && matches(key))
    )

    function matches(key) {
        if (!search.value) return true

        return label(key).toLowerCase().includes(search.value.toLowerCase())
    }

    function label(key) {
        return Vue.$__(TRUCK_COLUMNS[key].label)
    }

    function icon(key) {
        return TRUCK_COLUMNS[key].icon || DEFAULT_COLUMN_ICON
    }

    // ---- Changing the selection -------------------------------------------

    /**
     * Writing the config costs a request, so a "change" that leaves the same
     * columns in the same order (hide all with nothing shown, reset when
     * already at the default, a drag dropped where it started) is dropped here.
     */
    function save(fields) {
        const unchanged =
            fields.length === selected.value.length &&
            fields.every((key, index) => key === selected.value[index])

        if (unchanged) return

        saveConfig({ fields })
    }

    /** Shown columns go last, so the ones already arranged keep their place. */
    function toggle(key) {
        if (order.value.includes(key)) {
            save(order.value.filter((column) => column !== key))
            return
        }

        save([...order.value, key])
    }

    function hideAll() {
        save([])
    }

    /** Every column of the catalogue, the ones already picked keeping their place. */
    function showAll() {
        const rest = Object.keys(TRUCK_COLUMNS).filter((key) => !order.value.includes(key))

        save([...order.value, ...rest])
    }

    function reset() {
        save(defaultOf('vehicleListTableFields').fields)
    }

    // `draggable` has already reordered `order` in place
    function saveOrder() {
        save(order.value)
    }

    /** The whole selection at once, e.g. after a drag in the table header. */
    function setOrder(keys) {
        save(keys.filter((key) => TRUCK_COLUMNS[key]))
    }

    return {
        headers,
        order,
        search,
        shown,
        hidden,
        label,
        icon,
        toggle,
        hideAll,
        showAll,
        setOrder,
        reset,
        saveOrder
    }
}
